/* eslint-disable camelcase */
import { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { RefreshCw } from 'lucide-react'

import Breakdown from './CohortBreakdown'
import {
  type CohortPath,
  isMeetingSplitDimension,
  type MeetingSplitDimension,
  type Metric,
  metrics,
  metricValue,
  readMetric,
  treeUrlNotice,
} from './exploration'
import FilterForm, { AppliedFilterChips } from './FilterForm'
import { Trend } from './MeasurementPanels'
import { MeetingList, VersionComparison } from './MetadataTables'
import MetricChoices from './MetricChoices'
import {
  defaultFilterValues,
  filterSearchParams,
  type FilterValues,
  formatCount,
  readFilterState,
  toApiFilters,
} from './model'
import { ErrorState, LoadingState, StaleNotice } from './shared'
import { availableSharedSplits, MAX_TREND_GROUPS, readSharedSplits, writeSharedSplits } from './sharedSplits'
import SourceDiagnostics from './SourceDiagnostics'
import SummaryChipPanels, { summaryChipValue } from './SummaryChipPanels'
import {
  isAccessDenied,
  isMissingEndpoint,
  isTransientQueryError,
  useAttributionGroupedTrend,
  useAttributionOverview,
} from './useAttributionObservability'
import { useSummaryChipOverview } from './useSummaryChipMetrics'

import styles from './MetricExplorer.module.scss'

import { attributionDimensions, facetDimensions } from '@/api/attributionObservability'
import type { SummaryChipSource } from '@/api/summaryChipMetrics'
import Button from '@/components/Button'
import CollapsibleSection from '@/components/CollapsibleSection'
import { viewerHref, type ViewerMode } from '@/pages/app/AttributionViewer/mode'
import { selectIsAdmin } from '@/store/features/user/userSlice'
import { compactAmPm } from '@/utils/compactAmPm'

type MeetingSelection = {
  path: CohortPath
  filtersKey: string
  request: number
}

const AttributionObservabilityPage = () => {
  const [search, setSearch] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // The metrics endpoints are admin-only; any Wispr account gets the meeting lookup.
  const isAdmin = useSelector(selectIsAdmin)
  const [accessDenied, setAccessDenied] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [treeReset, setTreeReset] = useState(0)
  const [sourcesOpen, setSourcesOpen] = useState(false)
  const [meetingSelection, setMeetingSelection] = useState<MeetingSelection | null>(null)
  const { values, notice: filterNotice } = useMemo(() => readFilterState(search), [search])
  const filters = useMemo(() => toApiFilters(values), [values])
  const optionFilters = useMemo(
    () =>
      toApiFilters({
        ...defaultFilterValues(),
        start: values.start,
        end: values.end,
        participant_source: values.participant_source,
      }),
    [values.start, values.end, values.participant_source]
  )
  const { axes, notice: splitNotice } = readSharedSplits(search, filters)
  const urlNotice = isAdmin
    ? (filterNotice ??
      splitNotice ??
      (search.has('metric') && !metrics.some((item) => item === search.get('metric'))
        ? 'This saved view uses a metric that is no longer available.'
        : null) ??
      (search.has('split') ? null : treeUrlNotice(search, filters)))
    : undefined
  const metric = readMetric(search)
  const isChipMetric = metric === 'summary_speaker_chips_named'
  const chipSource: SummaryChipSource = search.get('chip_source') === 'current_state' ? 'current_state' : 'publication'
  const chipSourceLabel = chipSource === 'current_state' ? 'Current-state backfill' : 'Publication snapshot'
  const chips = useSummaryChipOverview(filters, isAdmin && !urlNotice && !accessDenied, chipSource)
  const groupedTrend = useAttributionGroupedTrend(
    filters,
    axes,
    isAdmin && !urlNotice && !isChipMetric && axes.length > 0
  )
  const overview = useAttributionOverview(filters, isAdmin && !urlNotice)
  const options = useAttributionOverview(
    optionFilters,
    isAdmin && (pickerOpen || facetDimensions.some((dimension) => values[dimension])) && !urlNotice
  )
  useEffect(
    () =>
      queryClient.getQueryCache().subscribe((event) => {
        if (event.query.queryKey[0] === 'attribution-observability' && isAccessDenied(event.query.state.error))
          setAccessDenied(true)
      }),
    [queryClient]
  )
  const retryOverview = async () => {
    const chipRetry = chips.refetch()
    const results = await Promise.all([
      overview.refetch(),
      ...(accessDenied && JSON.stringify(filters) !== JSON.stringify(optionFilters) ? [options.refetch()] : []),
    ])
    const chipResult = await chipRetry
    const trendResult = accessDenied && axes.length > 0 ? await groupedTrend.refetch() : undefined
    if (
      results.every((result) => !result.error) &&
      !isAccessDenied(chipResult.error) &&
      !isAccessDenied(trendResult?.error)
    ) {
      queryClient.removeQueries({
        type: 'inactive',
        predicate: (query) => query.queryKey[0] === 'attribution-observability' && isAccessDenied(query.state.error),
      })
      setAccessDenied(false)
    }
  }
  const getChipValue = () => {
    if (chips.isPending) return 'Loading…'
    if (chips.isError && !chips.data) return 'Unavailable'
    return summaryChipValue(chips.data?.status === 'available' ? chips.data.summary : undefined)
  }
  const filtersKey = JSON.stringify(filters)
  const validSelection =
    meetingSelection?.filtersKey === filtersKey &&
    meetingSelection.path.every(([dimension], depth) => axes[depth] === dimension)
  const selectedMeetings = validSelection ? meetingSelection : null
  useEffect(() => {
    if (meetingSelection && !validSelection) setMeetingSelection(null)
  }, [meetingSelection, validSelection])
  const closeMeetings = () => {
    setMeetingSelection(null)
  }
  const apply = (next: FilterValues) => {
    setMeetingSelection(null)
    const params = filterSearchParams(next)
    params.set('metric', metric)
    params.set('chip_source', chipSource)
    writeSharedSplits(
      params,
      axes.filter((axis) => availableSharedSplits(toApiFilters(next)).includes(axis))
    )
    setSearch(params)
  }
  const chooseMetric = (next: Metric) => {
    const params = new URLSearchParams(search)
    params.set('metric', next)
    setSearch(params)
  }
  const chooseAxes = (next: MeetingSplitDimension[]) => {
    setMeetingSelection(null)
    const params = new URLSearchParams(search)
    writeSharedSplits(params, next)
    setSearch(params)
  }
  const resetTree = () => {
    chooseAxes([])
    setTreeReset((value) => value + 1)
  }
  const filterTo = (path: CohortPath) => {
    const physical = path.filter(([dimension]) => isMeetingSplitDimension(dimension))
    const next: FilterValues = {
      ...values,
      ...Object.fromEntries(physical),
      negate: values.negate.filter((dimension) => !physical.some(([key]) => key === dimension)),
    }
    const params = filterSearchParams(next)
    params.set('metric', metric)
    params.set('chip_source', chipSource)
    writeSharedSplits(
      params,
      axes.filter((axis) => availableSharedSplits(toApiFilters(next)).includes(axis))
    )
    setMeetingSelection(null)
    setSearch(params)
  }
  const summary = overview.data?.summary
  const selected = summary ? metricValue(summary, metric, filters) : null
  const openMeeting = (meetingId: string, mode: ViewerMode) => navigate(viewerHref(meetingId, mode))
  return (
    <main className={styles.page}>
      <Helmet>
        <title>Speaker attribution</title>
      </Helmet>
      <div className={styles.content}>
        <header className={styles.header}>
          <div>
            <h1>Speaker attribution</h1>
            {!isAdmin && (
              <p className={styles.subtitle}>Open a meeting to inspect its attribution or annotate ground truth.</p>
            )}
          </div>
          {isAdmin && (
            <div className={styles.headerActions}>
              <Button
                title={<RefreshCw size={15} />}
                ariaLabel="Refresh dashboard"
                type="light"
                isDisabled={!!urlNotice}
                isLoading={overview.isFetching}
                onClick={() =>
                  accessDenied
                    ? void retryOverview()
                    : void queryClient.invalidateQueries({
                        queryKey: ['attribution-observability'],
                        refetchType: 'active',
                      })
                }
              />
            </div>
          )}
        </header>
        <FilterForm
          values={values}
          facets={
            accessDenied
              ? {}
              : ((!isAccessDenied(options.error) ? options.data?.facets : undefined) ?? overview.data?.facets ?? {})
          }
          onApply={apply}
          onReset={() => apply(defaultFilterValues())}
          onOpenChange={setPickerOpen}
          isLoadingOptions={pickerOpen && options.isFetching}
          onOpenMeeting={openMeeting}
          meetingLookupOnly={!isAdmin}
        />
        {isAdmin && !urlNotice && !accessDenied && (
          <section className={styles.metricSection} aria-label="Attribution overview">
            <MetricChoices
              metric={metric}
              summary={overview.data?.summary}
              filters={filters}
              chipValue={getChipValue()}
              chipSourceLabel={chipSourceLabel}
              onSelect={chooseMetric}
            />
          </section>
        )}
        {!isAdmin ? null : urlNotice ? (
          <section className={styles.empty} role="alert">
            <p>{urlNotice} Review the supported filters above, then continue.</p>
            <Button title="Use supported filters" type="light" onClick={() => apply(values)} />
          </section>
        ) : accessDenied ? (
          <ErrorState onRetry={() => void retryOverview()} />
        ) : isChipMetric ? (
          <>
            <div className={styles.chipSplits}>
              <label>
                Measurement source
                <select
                  value={chipSource}
                  onChange={(event) => {
                    const params = new URLSearchParams(search)
                    params.set('chip_source', event.target.value)
                    setSearch(params)
                  }}
                >
                  <option value="publication">Publication snapshot</option>
                  <option value="current_state">Current-state backfill</option>
                </select>
              </label>
            </div>
            <SummaryChipPanels
              source={chipSource}
              filters={filters}
              axes={axes}
              overview={chips}
              onAxes={chooseAxes}
              onFilter={filterTo}
              onUseSupported={(supported) => {
                const next = { ...values, negate: values.negate.filter((dimension) => supported.includes(dimension)) }
                for (const dimension of attributionDimensions) {
                  if (
                    !supported.includes(dimension) ||
                    (dimension === 'participant_bucket' && values.participant_source === 'invited')
                  )
                    next[dimension] = ''
                }
                const params = filterSearchParams(next)
                params.set('metric', metric)
                params.set('chip_source', chipSource)
                writeSharedSplits(
                  params,
                  axes.filter(
                    (axis) =>
                      supported.includes(axis) &&
                      (axis !== 'participant_bucket' || values.participant_source !== 'invited')
                  )
                )
                setSearch(params)
              }}
            />
          </>
        ) : overview.isError && (!overview.data || !isTransientQueryError(overview.error)) ? (
          <ErrorState onRetry={() => void retryOverview()} />
        ) : summary && overview.data ? (
          <>
            {overview.isError && <StaleNotice onRetry={() => void retryOverview()} />}
            <section className={styles.metricSection} aria-label="Selected metric detail">
              {selected && (
                <p className={styles.measurementNote}>
                  {metric === 'meetings' ? (
                    'Each collected meeting is counted once.'
                  ) : (
                    <>
                      {selected.detail} <span aria-hidden="true">·</span> {formatCount(selected.measured)} /{' '}
                      {formatCount(summary.meeting_count)} meetings measured
                      {(metric === 'labels' || metric === 'labels_by_words' || metric === 'fully_named') && (
                        <>
                          {' '}
                          <span aria-hidden="true">·</span> Machine labels; reviewed accuracy needs ground truth.
                        </>
                      )}
                      {(metric === 'ax' || metric === 'ax_capture') && (
                        <>
                          {' '}
                          <span aria-hidden="true">·</span>{' '}
                          {metric === 'ax'
                            ? 'Share of speech duration overlapping an active AX speaker indicator.'
                            : 'Share of active recording time with readable AX evidence, including silence.'}
                        </>
                      )}
                    </>
                  )}
                </p>
              )}
            </section>
            {summary.meeting_count ? (
              <Breakdown
                key={`${JSON.stringify(filters)}:${treeReset}`}
                filters={filters}
                metric={metric}
                splits={{}}
                axes={axes}
                rootFilters={
                  <AppliedFilterChips
                    values={values}
                    facets={options.data?.facets ?? overview.data.facets}
                    onApply={apply}
                    compact
                  />
                }
                branchBudget={MAX_TREND_GROUPS}
                leafBudget={120}
                summary={summary}
                onSplit={(path, dimension) => {
                  if (dimension === axes[path.length]) return
                  chooseAxes([
                    ...axes.slice(0, path.length),
                    ...(dimension && isMeetingSplitDimension(dimension) ? [dimension] : []),
                  ])
                }}
                onReset={resetTree}
                onFilter={filterTo}
                selectedPath={selectedMeetings?.path}
                meetingRequest={selectedMeetings?.request}
                onCloseMeetings={closeMeetings}
                onViewMeetings={(path) =>
                  setMeetingSelection((previous) => ({
                    path,
                    filtersKey,
                    request: (previous?.request ?? 0) + 1,
                  }))
                }
              />
            ) : (
              <section className={styles.empty}>
                <h2>No meetings match these filters</h2>
                <p>Try removing a tag or choosing another date range.</p>
              </section>
            )}
            <div className={styles.details}>
              {axes.length === 0 ? (
                <Trend metric={metric} trend={overview.data.trend} startAt={filters.start_at} endAt={filters.end_at} />
              ) : groupedTrend.isPending ? (
                <LoadingState label="Loading daily breakdown…" skeleton />
              ) : groupedTrend.isError ? (
                <section role="alert">
                  {isMissingEndpoint(groupedTrend.error) ? (
                    <p>
                      Grouped daily charts require the updated backend. The breakdown above is available; choose no
                      splits to view the overall trend.
                    </p>
                  ) : (
                    <ErrorState onRetry={() => void groupedTrend.refetch()} />
                  )}
                </section>
              ) : groupedTrend.data ? (
                <Trend
                  metric={metric}
                  trend={[]}
                  groups={groupedTrend.data.groups}
                  truncated={groupedTrend.data.truncated}
                  startAt={filters.start_at}
                  endAt={filters.end_at}
                />
              ) : null}
            </div>
            <div className={styles.details}>
              <CollapsibleSection
                label="Speaker label sources"
                summary="Source shares and rule diagnostics"
                expanded={sourcesOpen}
                onToggle={() => setSourcesOpen(!sourcesOpen)}
              >
                {sourcesOpen && (
                  <SourceDiagnostics key={filtersKey} filters={filters} summary={summary} onFilter={filterTo} />
                )}
              </CollapsibleSection>
            </div>
            <div className={styles.details}>
              <CollapsibleSection
                label="Meeting details"
                summary="Versions and individual meetings"
                expanded={detailsOpen}
                onToggle={() => setDetailsOpen(!detailsOpen)}
              >
                {detailsOpen && (
                  <div className={styles.detailPanels}>
                    <VersionComparison filters={filters} captureCoverage={metric === 'ax_capture'} />
                    <MeetingList
                      key={JSON.stringify(filters)}
                      filters={filters}
                      captureCoverage={metric === 'ax_capture'}
                    />
                  </div>
                )}
              </CollapsibleSection>
            </div>
          </>
        ) : (
          <LoadingState label="Loading meeting metrics…" skeleton />
        )}
        {isAdmin && (
          <footer className={styles.footer}>
            <span>
              {isChipMetric
                ? `${chipSourceLabel}. Counts are read from the main meetings database.`
                : 'Latest successfully collected summary results. Some completed meetings may be missing.'}
            </span>
            {!isChipMetric && !accessDenied && overview.data && (
              <span>
                Latest collection:{' '}
                {overview.data.freshness.latest_persisted_at
                  ? compactAmPm(new Date(overview.data.freshness.latest_persisted_at).toLocaleString())
                  : 'Not available'}{' '}
                · Overview refreshes every minute
              </span>
            )}
          </footer>
        )}
      </div>
    </main>
  )
}

export default AttributionObservabilityPage
